import { createRef, Component } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames/bind';
import autoBind from 'react-autobind';
import en from 'date-fns/locale/en-GB';
import moment from 'moment';
import { registerLocale, setDefaultLocale } from 'react-datepicker';

import {
  PAST_MAX_DATE_ERR, FUTURE_MAX_DATE_ERR, DATE_FORMAT_ERR, EARLIEST_DATE_RANGE_ERR,
  EARLIEST_DAY_ERROR, EARLIEST_MIN_PERIOD_ERR,
} from '../../../../data/filters/filterConstants';
import {
  validateDatesRanges, validateDateValue, formatDateTime, validateEarliestSearchDate,
  validateCurrentDayDate, validateMaxDaysRange,
} from '../../../../helpers/timeFilterHelpers';
import withComponentName from '../../../../decorators/withComponentName';
import RoundRadioButton from '../../../GeneralComponents/RoundRadioButton';
import CalendarMobileItem from './CalendarMobileItem';
import CustomTimeFilterErrors from './CustomTimeFiltersErrors';

import CalendarItem from './CalendarItem';

import Styles from '../../story-filters-styles.module.scss';
import { EARLIEST_SEARCH_DATE_GOLD_USER } from '../../../../data/environment';

registerLocale('en', en);
setDefaultLocale('en');

const cx = classNames.bind(Styles);

class CustomTimePeriod extends Component {
  constructor(props) {
    super(props);
    this.state = {
      startDate: moment().subtract(30, 'days').set({ seconds: 0, milliseconds: 0 }).toDate(),
      endDate: moment().set({ seconds: 0, milliseconds: 0 }).toDate(),
      inputStartDateValue: moment().subtract(30, 'days').set({ seconds: 0, milliseconds: 0 }).format('DD-MM-YYYY'),
      inputEndDateValue: moment().set({ seconds: 0, milliseconds: 0 }).format('DD-MM-YYYY'),
      visibleEndInput: true,
      visibleStartInput: true,
      minDate: moment(EARLIEST_SEARCH_DATE_GOLD_USER).toDate(),
      endTime: moment().set({ seconds: 0, milliseconds: 0 }).format('HH:mm'),
      startTime: moment().set({ seconds: 0, milliseconds: 0 }).format('HH:mm'),
      futureDateErr: false,
      pastMaxErr: false,
      dateFormatErr: false,
      endBeforeStartErr: false,
      calendarTypeOpened: '',
      timeFormatErr: false,
      sameDayErr: false,
      earliestDateErr: false,
      maxDaysRangeErr: false,
    };
    autoBind(this);
    this.dateStartInput = createRef();
    this.dateEndInput = createRef();
  }

  componentDidMount() {
    const {
      selectedFilterValue: { start_datetime: startDate, end_datetime: endDate },
      activeTimeFilter, earliestSearchDate, minDate, isFilingsPage, goldPlan,
    } = this.props;

    // check for earliest search date
    if (activeTimeFilter === 'custom' || isFilingsPage) {
      const invalidPeriod = validateEarliestSearchDate(
        earliestSearchDate,
        moment(startDate).toDate(),
        endDate,
        undefined,
        isFilingsPage,
        goldPlan,
      );

      const periodErrors = invalidPeriod === EARLIEST_DATE_RANGE_ERR
        || invalidPeriod === EARLIEST_DAY_ERROR;

      if (periodErrors) this.setState({ earliestDateErr: true });

      const startDateObj = startDate ? {
        startDate: moment(startDate).toDate(),
        startTime: moment(startDate).format('HH:mm'),
        inputStartDateValue: moment(startDate).format('DD-MM-YYYY'),
      } : {};
      const endDateObj = endDate ? {
        endDate: moment(endDate).toDate(),
        endTime: moment(endDate).format('HH:mm'),
        inputEndDateValue: moment(endDate).format('DD-MM-YYYY'),
      } : {};

      this.setState({
        ...startDateObj,
        ...endDateObj,
      });
    }
    if (minDate) {
      this.setState({
        minDate,
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { startDate, endDate } = this.state;

    if (startDate !== prevState.startDate || endDate !== prevState.endDate) {
      const validDateRange = validateDatesRanges(startDate, endDate);
      const invalidCurrentDayTime = validateCurrentDayDate(startDate, endDate);
      // validate earliest date
      if (!validDateRange) {
        this.setState({
          endBeforeStartErr: true,
          sameDayErr: false,
          startDate: prevState.startDate,
          endDate: prevState.endDate,
        });
      }

      const { isFilingsPage } = this.props;
      if (invalidCurrentDayTime && !isFilingsPage) {
        this.setState({
          sameDayErr: true,
          endBeforeStartErr: false,
          startDate: prevState.startDate,
          endDate: prevState.endDate,
        });
      }
    }
  }

  onDatesChange(date, period, inputPeriod, timePeriodItem) {
    const {
      setTimeFilter, earliestSearchDate, showButtonError, isFilingsPage, goldPlan,
    } = this.props;
    const { endDate, [timePeriodItem]: time } = this.state;

    const validatePeriod = validateEarliestSearchDate(
      earliestSearchDate,
      date,
      endDate,
      period,
      isFilingsPage,
      goldPlan,
    );

    if (validatePeriod === EARLIEST_MIN_PERIOD_ERR) {
      return this.setState({ earliestDateErr: true });
    }

    if (validatePeriod === PAST_MAX_DATE_ERR) {
      return this.validateCustomDate({ pastMaxErr: true });
    }

    this.setState({
      [period]: formatDateTime(date, time),
      [inputPeriod]: moment(date).format('DD-MM-YYYY'),
      earliestDateErr: false,
      sameDayErr: false,
      pastMaxErr: false,
      maxDaysRangeErr: false,
    }, () => {
      const { startDate, endDate } = this.state;

      if (validateCurrentDayDate(startDate, endDate) && !isFilingsPage) {
        return this.setState({
          sameDayErr: true,
        });
      }

      const isValidDaysRange = validateMaxDaysRange(period, date, startDate, endDate);
      if (!isValidDaysRange && !isFilingsPage) {
        showButtonError(true);
        return this.setState({
          maxDaysRangeErr: true,
        });
      }

      showButtonError(false);

      setTimeFilter({
        selected_time_filter: 'custom',
        start_datetime: moment(startDate).toISOString(),
        end_datetime: moment(endDate).toISOString(),
        displayedName: `${moment(startDate).format('DD-MM-YYYY').substring(0, 6)}...`,
      });
    });
  }

  // validate user typed date
  validateCustomDate(errorValue) {
    this.setState({
      futureDateErr: false,
      pastMaxErr: false,
      dateFormatErr: false,
      endBeforeStartErr: false,
      earliestDateErr: false,
      maxDaysRangeErr: false,
      ...errorValue,
    });
  }

  // fire when user type date
  typeDate({ target }, period, fieldText, timePeriodItem) {
    const {
      setTimeFilter, earliestSearchDate, showButtonError, isFilingsPage, goldPlan,
    } = this.props;
    const { [timePeriodItem]: timePeriod, endDate } = this.state;

    const val = target.value;

    const validDateValue = validateDateValue(val, isFilingsPage);

    this.setState({ [fieldText]: val });

    if (validDateValue === DATE_FORMAT_ERR) {
      this.validateCustomDate({ dateFormatErr: true });
      showButtonError(true);
      return;
    }

    const [day, month, year] = val.split('-');
    const currentDate = moment([year, month - 1, day]).toDate();

    // validate earliest date
    const validatePeriod = validateEarliestSearchDate(
      earliestSearchDate,
      currentDate,
      endDate,
      period,
      isFilingsPage,
      goldPlan,
    );

    if (validDateValue === FUTURE_MAX_DATE_ERR && validDateValue) {
      return this.validateCustomDate({ futureDateErr: true });
    }

    if (validatePeriod === EARLIEST_MIN_PERIOD_ERR) {
      return this.validateCustomDate({ earliestDateErr: true });
    }

    if (
      validDateValue === PAST_MAX_DATE_ERR && validDateValue
    ) {
      return this.validateCustomDate({ pastMaxErr: true });
    }

    if (validDateValue) {
      this.setState({
        [period]: formatDateTime(validDateValue, timePeriod),
        futureDateErr: false,
        pastMaxErr: false,
        endBeforeStartErr: false,
        dateFormatErr: false,
        earliestDateErr: false,
        sameDayErr: false,
        maxDaysRangeErr: false,
      }, () => {
        // eslint-disable-next-line no-shadow
        const { startDate, endDate } = this.state;

        if (validateCurrentDayDate(startDate, endDate) && !isFilingsPage) {
          return this.setState({
            sameDayErr: true,
            maxDaysRangeErr: false,
          });
        }

        const isValidDaysRange = validateMaxDaysRange(period, currentDate, startDate, endDate);

        if (!isValidDaysRange && !isFilingsPage) {
          showButtonError(true);
          return this.setState({
            maxDaysRangeErr: true,
          });
        }

        showButtonError(false);

        setTimeFilter({
          selected_time_filter: 'custom',
          start_datetime: moment(startDate).toISOString(),
          end_datetime: moment(endDate).toISOString(),
          displayedName: `${moment(startDate).format('DD-MM-YYYY').substring(0, 6)}...`,
        });
      });
    }
  }

  typeTime({ target }, fieldText, period) {
    const { [period]: date } = this.state;
    const { setTimeFilter, isFilingsPage, showButtonError } = this.props;

    const val = target.value;

    const validTime = moment(val, 'HH:mm', true).isValid();

    this.setState({ [fieldText]: val });

    if (!validTime && val.replace(/\D/g, '').length === 4) {
      showButtonError(true);
      this.setState({ timeFormatErr: true });
      return;
    }

    if (validTime) {
      this.setState({
        [period]: formatDateTime(date, val),
        timeFormatErr: false,
        sameDayErr: false,
        maxDaysRangeErr: false,
      }, () => {
        const { startDate, endDate } = this.state;

        if (validateCurrentDayDate(startDate, endDate) && !isFilingsPage) {
          return this.setState({
            sameDayErr: true,
          });
        }

        const isValidDaysRange = validateMaxDaysRange(period, date, startDate, endDate);

        if (!isValidDaysRange && !isFilingsPage) {
          showButtonError(true);
          return this.setState({
            maxDaysRangeErr: true,
          });
        }

        setTimeFilter({
          selected_time_filter: 'custom',
          start_datetime: moment(startDate).toISOString(),
          end_datetime: moment(endDate).toISOString(),
          displayedName: `${moment(startDate).format('DD-MM-YYYY').substring(0, 6)}...`,
        });
      });
    }
  }

  closeOnClick(e) {
    const {
      visibleEndInput, inputStartDateValue, inputEndDateValue, startDate, endDate,
      visibleStartInput,
    } = this.state;
    const { isFilingsPage, goldPlan, earliestSearchDate } = this.props;
    e.stopPropagation();

    if (
      (
        visibleStartInput
        && this.dateStartInput.current
        && !this.dateStartInput.current.getInputDOMNode().contains(e.target)
      ) || (
        visibleEndInput
        && this.dateEndInput.current
        && !this.dateEndInput.current.getInputDOMNode().contains(e.target)
      )
    ) {
      const validStartDate = validateDateValue(inputStartDateValue, isFilingsPage) !== PAST_MAX_DATE_ERR
        && validateDateValue(inputStartDateValue, isFilingsPage) !== FUTURE_MAX_DATE_ERR
        && validateDatesRanges(startDate, endDate)
        && moment(inputStartDateValue, 'DD-MM-YYYY', true).isValid()
        && !validateCurrentDayDate(startDate, endDate)
        && validateEarliestSearchDate(
          earliestSearchDate,
          startDate,
          endDate,
          true,
          isFilingsPage,
          goldPlan,
        );

      const validEndDate = validateDateValue(inputEndDateValue, isFilingsPage) !== PAST_MAX_DATE_ERR
        && validateDateValue(inputEndDateValue, isFilingsPage) !== FUTURE_MAX_DATE_ERR
        && validateDatesRanges(endDate, startDate)
        && moment(inputEndDateValue, 'DD-MM-YYYY', true).isValid()
        && !validateCurrentDayDate(startDate, endDate);

      this.setState({
        visibleEndInput: false,
        visibleStartInput: false,
        futureDateErr: false,
        pastMaxErr: false,
        endBeforeStartErr: false,
        sameDayErr: false,
        dateFormatErr: false,
        earliestDateErr: false,
        inputStartDateValue: validStartDate
          ? inputStartDateValue
          : moment(startDate).format('DD-MM-YYYY'),
        inputEndDateValue: validEndDate
          ? inputEndDateValue
          : moment(endDate).format('DD-MM-YYYY'),
      });
      document.removeEventListener('click', this.closeOnClick);
    }
  }

  // show date input on user click
  showInput(inputType) {
    const { width } = this.props;
    const { [inputType]: stateInput } = this.state;

    if (width <= 767 && stateInput) return;

    this.setState({
      [inputType]: true,
      calendarTypeOpened: inputType,
    }, () => {
      const { visibleEndInput, visibleStartInput } = this.state;
      document.addEventListener('click', this.closeOnClick);

      if (visibleEndInput) this.dateEndInput.current.getInputDOMNode().focus();
      if (visibleStartInput) this.dateStartInput.current.getInputDOMNode().focus();
    });
  }

  render() {
    const {
      endDate, inputStartDateValue, startDate, minDate, inputEndDateValue,
      visibleEndInput, visibleStartInput, endTime, startTime, pastMaxErr, futureDateErr,
      endBeforeStartErr, dateFormatErr, calendarTypeOpened, timeFormatErr, earliestDateErr,
      sameDayErr, maxDaysRangeErr,
    } = this.state;
    const {
      setTimeFilter, activeTimeFilter, width, customTimeFilter, earliestSearchDate,
      hideRoundButton, timeInputWrapperClass, errorsBlockClass, isFilingsPage,
      hasBigScreenDesign, buttonErr, silver, disabled,
    } = this.props;

    const selectedClass = cx({
      'time-filter-item': true,
      'custom-time-filter': true,
      active: activeTimeFilter === 'custom',
    });

    const errorMargin = futureDateErr || pastMaxErr || dateFormatErr
      || endBeforeStartErr || earliestDateErr || maxDaysRangeErr;

    const isInputDisabled = disabled || activeTimeFilter !== 'custom';

    return (
      <div className={selectedClass}>
        {
          !hideRoundButton
            ? (
              <div className={Styles['round-button-wrapper']}>
                <RoundRadioButton
                  name="Specific time & date"
                  value="custom"
                  setValue={setTimeFilter}
                  selectedFilterValue={activeTimeFilter}
                  filterType="custom"
                  filterItemInfo={{
                    selected_time_filter: 'custom',
                    start_datetime: startDate,
                    end_datetime: endDate,
                    displayedName: `${moment(startDate).format('DD-MM-YYYY').substring(0, 6)}...`,
                  }}
                  buttonDisabled={!customTimeFilter}
                  hasBigScreenDesign={hasBigScreenDesign}
                />
                <Link className={cx('premium-icon-badge', 'selected-time-period-icon-badge')} to="/pricing">
                  <div className={Styles['premium-silver-icon-badge-container']}>
                    <div className={Styles['premium-silver-icon-badge']}>
                      SILVER
                    </div>
                    <div className={Styles['silver-tooltip']}>
                      {silver ? 'This is available only to Premium subscribers' : 'Upgrade your plan to Silver to use this feature'}
                    </div>
                  </div>
                </Link>
              </div>
            )
            : null
        }
        <div className={Styles['custom-time-wrapper']}>
          <CalendarItem
            title="From"
            selectsEnd={false}
            selectsStart
            dateType="startDate"
            selectedDate={startDate}
            startDate={startDate}
            endDate={endDate}
            maxDate={endDate}
            visibleInputItem={visibleStartInput}
            inputDateValue={inputStartDateValue}
            typeDate={this.typeDate}
            inputDatePeriod="inputStartDateValue"
            showInput={this.showInput}
            visibleInput="visibleStartInput"
            typeTime={this.typeTime}
            timePeriodValue={startTime}
            timePeriodItem="startTime"
            onDatesChange={this.onDatesChange}
            ref={this.dateStartInput}
            minDate={minDate}
            visibleDate={startDate}
            width={width}
            earliestSearchDate={earliestSearchDate}
            earliestDateErr={earliestDateErr}
            errorMargin={errorMargin}
            timeInputWrapperClass={timeInputWrapperClass}
            isInputDisabled={isInputDisabled}
          />

          <CustomTimeFilterErrors
            futureDateErr={futureDateErr}
            pastMaxErr={pastMaxErr}
            endBeforeStartErr={endBeforeStartErr}
            dateFormatErr={dateFormatErr}
            timeFormatErr={timeFormatErr}
            earliestDateErr={earliestDateErr}
            errorsBlockClass={errorsBlockClass}
            sameDayErr={sameDayErr}
            isFilingsPage={isFilingsPage}
            maxDaysRangeErr={maxDaysRangeErr}
          />

          <CalendarItem
            title="To"
            selectsEnd
            selectsStart={false}
            dateType="endDate"
            selectedDate={endDate}
            startDate={startDate}
            endDate={endDate}
            maxDate={new Date()}
            visibleInputItem={visibleEndInput}
            inputDateValue={inputEndDateValue}
            typeDate={this.typeDate}
            inputDatePeriod="inputEndDateValue"
            showInput={this.showInput}
            visibleInput="visibleEndInput"
            typeTime={this.typeTime}
            timePeriodValue={endTime}
            timePeriodItem="endTime"
            onDatesChange={this.onDatesChange}
            ref={this.dateEndInput}
            minDate={startDate}
            width={width}
            errorMargin={errorMargin}
            timeInputWrapperClass={timeInputWrapperClass}
            isInputDisabled={isInputDisabled}
          />

          {width <= 767 && (
            <CalendarMobileItem
              calendarTypeOpened={calendarTypeOpened}
              startDate={startDate}
              endDate={endDate}
              onDatesChange={this.onDatesChange}
              minDate={minDate}
              maxDate={new Date()}
              visibleEndInput={visibleEndInput}
              visibleStartInput={visibleStartInput}
              buttonErr={buttonErr}
            />
          )}
        </div>
      </div>
    );
  }
}

export default withComponentName(CustomTimePeriod);
